import * as React from "react";
import { connect } from "react-redux";
import { validators } from "not-valid";
import { values } from "lodash";

import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { getQuestionConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import * as questionIds from "@teamprevent/common/healthSurveillance/questions/questionIds";
import { HealthSurveillanceSectionProps } from "@teamprevent/common/healthSurveillance/models";
import { LinkObject } from "@teamprevent/common/model";
import { GENDER_OPTIONS } from "@teamprevent/common/utils";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { Detail } from "@neworbit/simpleui-detail";
import { DropdownOption } from "@teamprevent/common/questionnaires/model";
import { WorkGroupDetailModel } from "@teamprevent/common/workGroup/model";

const sectionId = "AssociatePersonalDetailsSection";

// eslint-disable-next-line max-len
const AssociatePersonalDetailsSectionUnconnected: React.FunctionComponent<
    HealthSurveillanceSectionProps &
    { getCustomers: () => Promise<LinkObject[]> } &
    { getOrgLevels: (customerId: string) => Promise<LinkObject[]> } &
    { getSubOrgLevels: (customerId: string, parentOrganisationId: string) => Promise<LinkObject[]> } &
    { getWorkGroups: (customerId: string) => Promise<WorkGroupDetailModel[]> } &
    { onCustomerSelect: (customerId: string, workGroups: WorkGroupDetailModel[]) => void }> = (props) => {
        const [customers, setCustomers] = React.useState<DropdownOption[]>([]);
        const [orgLevels, setOrgLevels] = React.useState<DropdownOption[]>([]);
        const [orgLevels2, setOrgLevels2] = React.useState<DropdownOption[]>([]);
        const [workGroups, setWorkGroups] = React.useState<DropdownOption[]>([]);
        const questions = values(props.questions || {});
        const [selectedCustomerId, setSelectedCustomerId] = React.useState<string>("");

        const updateAnswer = async (
            questionId: string,
            answer: string | string[]) => {

            props.updateAnswer({
                sectionId,
                id: questionId,
                question: {
                    id: questionId,
                    isValid: true,
                    question: (getQuestionConfig(props.questionConfigs, questionId) as CustomQuestionConfig).questionText,
                    answer
                },
                questionnaireKey: props.questionnaireKey
            });
        };

        const onCustomerChange = async (customerId: string, clearOrgLevelAndWorkGroup: boolean = true) => {
            if (!props.readonly) {
                setSelectedCustomerId(customerId);
                const orgLevelsResponse = await props.getOrgLevels(customerId);
                setOrgLevels(orgLevelsResponse.map(o => ({ text: o.name, value: o.id })));
                const workGroupsResponse = await props.getWorkGroups(customerId);
                setWorkGroups(workGroupsResponse.map(o => ({ text: o.name, value: o.id })));

                if (!clearOrgLevelAndWorkGroup) {
                    const savedOrgLevel1Id = getAnswerToQuestionWithId(questions, questionIds.OrgLevelId1)?.toString();
                    if (savedOrgLevel1Id) {
                        const orgLevelsResponse2 = await props.getSubOrgLevels(customerId, savedOrgLevel1Id);
                        setOrgLevels2(orgLevelsResponse2.map(o => ({ text: o.name, value: o.id })));
                    }
                }

                props.onCustomerSelect(customerId, workGroupsResponse);

                updateAnswer(
                    questionIds.CustomerName,
                    customers.find(o => o.value === customerId)?.text);

                if (clearOrgLevelAndWorkGroup) {
                    updateAnswer(
                        questionIds.OrgLevelName1,
                        null);

                    updateAnswer(
                        questionIds.OrgLevelId1,
                        null);

                    updateAnswer(
                        questionIds.OrgLevelName2,
                        null);

                    updateAnswer(
                        questionIds.OrgLevelId2,
                        null);

                    updateAnswer(
                        questionIds.WorkGroupName,
                        null);

                    updateAnswer(
                        questionIds.WorkGroupId,
                        []);
                }
            }
        };

        const onOrgLevelChange = async (orgLevelId: string) => {
            if (!props.readonly && selectedCustomerId) {
                const orgLevelsResponse = await props.getSubOrgLevels(selectedCustomerId, orgLevelId);
                setOrgLevels2(orgLevelsResponse.map(o => ({ text: o.name, value: o.id })));

                updateAnswer(
                    questionIds.OrgLevelName1,
                    orgLevels.find(o => o.value === orgLevelId)?.text);
            }
        };

        const onOrgLevel2Change = async (orgLevelId2: string) => {
            if (!props.readonly) {
                updateAnswer(
                    questionIds.OrgLevelName2,
                    orgLevels2.find(o => o.value === orgLevelId2)?.text);
            }
        };

        const onWorkGroupChange = (workGroupId: string[]) => {
            if (!props.readonly) {

                const workGroupNames =
                    workGroups.filter(w => workGroupId.some(s => s === w.value))
                        .map(w => w.text);

                updateAnswer(
                    questionIds.WorkGroupName,
                    workGroupNames);
            }
        };

        React.useEffect(() => {
            (async () => {
                if (!props.readonly) {
                    const response = await props.getCustomers();
                    setCustomers(response.map(o => ({ text: o.name, value: o.id })));

                    const savedCustomerId = getAnswerToQuestionWithId(questions, questionIds.CustomerId)?.toString();
                    if (savedCustomerId) {
                        await onCustomerChange(savedCustomerId, false);
                    }
                }
            })();
        }, []);

        return (
            <OldProgressBarQuestionnaireSectionUnconnected
                {...props}
                id={sectionId}
                title="Associate Details"
            >
                <TextQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ReferredTitle)}
                    readonly={props.readonly}
                    mandatory
                />
                <TextQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ReferredFirstName)}
                    readonly={props.readonly}
                    mandatory
                />
                <TextQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ReferredLastName)}
                    readonly={props.readonly}
                    mandatory
                />
                <DropdownQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ReferredGender)}
                    options={GENDER_OPTIONS}
                    readonly={props.readonly}
                    mandatory
                />
                <OldDateQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ReferredDob)}
                    readonly={props.readonly}
                    mandatory
                />
                <TextQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Postcode)}
                    readonly={props.readonly}
                    mandatory
                />
                <DropdownQuestion
                    hideDivider
                    search
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.CustomerId)}
                    options={customers}
                    hidden={props.readonly}
                    readonly={props.readonly}
                    onAnswerChange={onCustomerChange}
                    mandatory
                />
                <TextQuestion
                    hideDivider
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.CustomerName)}
                    hidden={!props.readonly}
                />
                <DropdownQuestion
                    search
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OrgLevelId1)}
                    options={orgLevels}
                    hidden={props.readonly}
                    readonly={props.readonly}
                    onAnswerChange={onOrgLevelChange}
                    mandatory
                    hideDivider
                />
                <TextQuestion
                    hideDivider
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OrgLevelName1)}
                    hidden={!props.readonly}
                />
                <DropdownQuestion
                    hideDivider
                    search
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OrgLevelId2)}
                    options={orgLevels2}
                    hidden={props.readonly}
                    readonly={props.readonly}
                    onAnswerChange={onOrgLevel2Change}
                />
                <TextQuestion
                    hideDivider
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OrgLevelName2)}
                    hidden={!props.readonly}
                />
                <TextQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EmployeeId)}
                    readonly={props.readonly}
                    validators={[ validators.validLength({ max: 20 }) ]}
                    mandatory
                />
                <TextQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.PersonalEmailAddress)}
                    readonly={props.readonly}
                    mandatory
                />
                <TextQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MobilePhone)}
                    readonly={props.readonly}
                    validators={[validators.validPhoneNumber(10)]}
                    mandatory
                />
                <DropdownQuestion
                    hideDivider
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WorkGroupId)}
                    options={workGroups}
                    hidden={props.readonly}
                    readonly={props.readonly}
                    onAnswerChange={onWorkGroupChange}
                    mandatory
                    multiple
                />
                {props.readonly &&
                    <Detail label="Work Groups"
                        value={Object.values(getAnswerToQuestionWithId(questions, questionIds.WorkGroupName))
                            ?.map(workGroup => workGroup).join(", ")} />
                }
                <TextQuestion
                    hideDivider
                    hidden
                    readonly
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.WorkGroupName)}
                />
            </OldProgressBarQuestionnaireSectionUnconnected>
        );
    };

// eslint-disable-next-line max-len
export const AssociatePersonalDetailsSection = connect(makeMapStateToQuestionnaireSectionProps(sectionId), mapDispatchToQuestionnaireSectionProps)(AssociatePersonalDetailsSectionUnconnected);
